import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import AgeContent from './AgeContent/AgeContent'
import GenderContent from './GenderContent/GenderContent'
import InterestsContent from './InterestsContent/InterestsContent'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { AGE } from '../../../../../Multiplatform/LineItemForms/fields'
import { OPTION_CUSTOM } from '../../../../../../constants/forms'
import { getGenderType } from '../../typeHelpers'
import { formatList } from '../../fields'
import { formatAgeToBE, formatGenderToBE } from '../../../LineItemFacebookCreate/LineItemFacebookCreateForm/formatters'
import { formatInterestsToBE } from '../../../../formatters'

import { clearUpdateLineItem, updateLineItem } from '../../../../../../modules/actions/lineItems'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import { campaignSelector } from '../../../../../../modules/selectors/campaigns'

import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { INTERESTS } from '../../../../../ReusableFormFields/InterestsFields/fields'
import { GENDER, GENDER_OPTION } from '../../../../../ReusableFormFields/GenderFields/fields'
import { AGE_MAX, AGE_MIN } from '../../../../fields'

const AudienceSectionForm = ({ editItemData: lineItem, title, ...formProps }) => {
  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const { objective: campaignObjective } = useSelector(campaignSelector)

  const handleSubmit = useCallback(
    values => {
      const updateLineItemData = {
        account: selectedAdAccountId,
        campaign_objective: campaignObjective,
        [AGE]: formatAgeToBE({
          ageOption: OPTION_CUSTOM,
          ageMin: values[AGE_MIN],
          ageMax: values[AGE_MAX]
        }),
        [GENDER]: formatGenderToBE(values[GENDER_OPTION]),
        [INTERESTS]: formatInterestsToBE(values[INTERESTS]),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateLineItem(updateLineItemData, lineItem.id, FACEBOOK_PLATFORM))
    },
    [dispatch, lineItem.id, campaignObjective, selectedAdAccountId, title]
  )

  const initialValues = useMemo(
    () => ({
      // AGE
      [AGE_MIN]: (lineItem[AGE] && lineItem[AGE][AGE_MIN]) || '',
      [AGE_MAX]: (lineItem[AGE] && lineItem[AGE][AGE_MAX]) || '',

      // GENDER
      [GENDER_OPTION]: lineItem[GENDER] && getGenderType(lineItem[GENDER]),

      // INTERESTS
      [INTERESTS]: formatList(lineItem[INTERESTS])
    }),
    [lineItem]
  )

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit
  })

  const purifiedFormik = usePurifiedFormik(formik)

  const { values, setFieldValue } = purifiedFormik

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <AgeContent formik={purifiedFormik} />
      <GenderContent formik={purifiedFormik} />
      <InterestsContent values={values} setFieldValue={setFieldValue} />
    </EditForm>
  )
}

export default AudienceSectionForm
